import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ChevronDown, ChevronUp, Sparkles, X } from "lucide-react";
import type {
  BuyerResearchBriefDto,
  BuyerResearchConversationGuards,
  BuyerResearchConversationResume,
  BuyerResearchIndustryCodesDto,
  BuyerResearchRunDto,
  BuyerResearchSellerFinancing,
} from "@/api/buyerResearch";
import { dailyLimitNextAllowedAt, isDailyLimitRefusal } from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { Button, Input, MissingValue, Textarea } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import { useDateFormatter } from "@/utils/datetime";
import { ResearchConversation } from "./ResearchConversation";
import { useResearchCount } from "./useResearchCount";
import { useResearchConversationState } from "./useResearchConversationState";
import { researchCopy } from "./researchCopy";
import {
  moneyInputValue,
  parseMoneyInput,
  provenanceCaption,
  summarizeProvenance,
} from "./researchFormat";
import { useStartResearchRun } from "./useBuyerResearch";

const SPECIALIST = "specialist";
const PROPOSED_BY_AI = "proposedByAi";

const SELLER_FINANCING_OPTIONS: BuyerResearchSellerFinancing[] = ["required", "preferred", "any"];

/**
 * The provenance caption under a field: where Phoenix got the value, and a
 * `Sparkles` mark when the model proposed it rather than the record.
 *
 * 🔴 <b>A FIELD WITH NO RECORDED SOURCE NOW RENDERS NOTHING.</b> This used to
 * render "Not on file" under every such row, which made that sentence the most
 * repeated string on the research page — eight of them on one brief, each one
 * reporting the same non-event. Table and Tile Standard §6.1 names the cost:
 * a mark that appears on ordinary rows stops being read, and by the time one
 * appears that means something the reader has learned to skip it.
 *
 * <p>The fact is not lost, it is aggregated: `summarizeProvenance` carries it
 * once for the whole brief. See `ResearchBriefReadOnly`.</p>
 *
 * <p>Returning `null` rather than an empty span deliberately — an empty span
 * still occupies a line box under a two-line grid row, so suppressing the text
 * alone would have removed the noise and kept the vertical cost.</p>
 */
function ProvenanceCaption({ label }: { label: string | undefined }) {
  const caption = provenanceCaption(label);
  if (caption == null) return null;
  return (
    <span
      className="inline-flex items-center gap-space-1 text-caption text-gray-mid"
      data-testid="research-provenance"
      data-provenance={label ?? ""}
    >
      {label === PROPOSED_BY_AI && <Sparkles size={12} aria-hidden />}
      {caption}
    </span>
  );
}

interface IndustryDraft {
  value: string;
  prov: string;
}

interface GeographyDraft {
  state: string;
  metro: string;
  prov: string;
}

interface Draft {
  industries: IndustryDraft[];
  geographies: GeographyDraft[];
  willRelocate: boolean | null;
  askingPriceMin: string;
  askingPriceMax: string;
  cashFlowMin: string;
  revenueMin: string;
  sellerFinancing: BuyerResearchSellerFinancing;
  exclusions: string;
  notes: string;

  /**
   * The resolved codes, carried through the drawer verbatim except where a
   * specialist prunes one.
   *
   * 🔴 RA6 — a pruned term keeps its entry with an EMPTY `codes` array. Dropping
   * the entry means "nobody has looked this up", and the server would resolve it
   * again on the next run, undoing the pruning silently.
   */
  industryCodes: BuyerResearchIndustryCodesDto[] | null;

  /** Provenance for the scalar fields, keyed by field name. */
  scalarProv: Record<string, string>;
}

const SCALAR_FIELDS = [
  "willRelocate",
  "askingPriceMin",
  "askingPriceMax",
  "cashFlowMin",
  "revenueMin",
  "sellerFinancing",
  "exclusions",
  "notes",
] as const;

function toDraft(brief: BuyerResearchBriefDto): Draft {
  const p = brief.provenance ?? {};
  const scalarProv: Record<string, string> = {};
  for (const f of SCALAR_FIELDS) {
    if (p[f]) scalarProv[f] = p[f];
  }
  return {
    industries: brief.industries.map((value, i) => ({ value, prov: p[`industries[${i}]`] ?? "" })),
    geographies: brief.geographies.map((g, i) => ({
      state: g.state ?? "",
      metro: g.metro ?? "",
      prov: p[`geographies[${i}]`] ?? "",
    })),
    willRelocate: brief.willRelocate,
    askingPriceMin: moneyInputValue(brief.askingPriceMin),
    askingPriceMax: moneyInputValue(brief.askingPriceMax),
    cashFlowMin: moneyInputValue(brief.cashFlowMin),
    revenueMin: moneyInputValue(brief.revenueMin),
    sellerFinancing: brief.sellerFinancing,
    exclusions: brief.exclusions ?? "",
    notes: brief.notes ?? "",
    industryCodes: brief.industryCodes ?? null,
    scalarProv,
  };
}

type MoneyField = "askingPriceMin" | "askingPriceMax" | "cashFlowMin" | "revenueMin";
const MONEY_FIELDS: MoneyField[] = ["askingPriceMin", "askingPriceMax", "cashFlowMin", "revenueMin"];

/** The brief as the server will receive it, or the field that refuses to parse. */
function fromDraft(
  d: Draft,
  statedPriceBand: string | null,
): { brief: BuyerResearchBriefDto } | { invalid: MoneyField } {
  const money: Partial<Record<MoneyField, number | null>> = {};
  for (const f of MONEY_FIELDS) {
    const parsed = parseMoneyInput(d[f]);
    if (parsed === undefined) return { invalid: f };
    money[f] = parsed;
  }
  const industries = d.industries.map((i) => i.value.trim()).filter(Boolean);
  const geographies = d.geographies
    .map((g) => ({ state: g.state.trim() || null, metro: g.metro.trim() || null }))
    .filter((g) => g.state || g.metro);

  const provenance: Record<string, string> = { ...d.scalarProv };
  d.industries
    .filter((i) => i.value.trim())
    .forEach((i, idx) => {
      if (i.prov) provenance[`industries[${idx}]`] = i.prov;
    });
  d.geographies
    .filter((g) => g.state.trim() || g.metro.trim())
    .forEach((g, idx) => {
      if (g.prov) provenance[`geographies[${idx}]`] = g.prov;
    });

  return {
    brief: {
      industries,
      geographies,
      willRelocate: d.willRelocate,
      askingPriceMin: money.askingPriceMin ?? null,
      askingPriceMax: money.askingPriceMax ?? null,
      cashFlowMin: money.cashFlowMin ?? null,
      revenueMin: money.revenueMin ?? null,
      sellerFinancing: d.sellerFinancing,
      exclusions: d.exclusions.trim() || null,
      notes: d.notes.trim() || null,
      provenance,
      statedPriceBand,
      // An entry whose term the specialist deleted goes with it — it describes
      // an industry that is no longer on the brief. Everything else rides
      // through untouched, INCLUDING a pruned entry with no codes (RA6).
      industryCodes: keepCodesForTerms(d.industryCodes, industries),
    },
  };
}

/**
 * Drops code entries whose term is no longer on the brief, matching the server's
 * own case-insensitive term comparison so a term re-cased in the drawer does not
 * silently lose the codes somebody already pruned.
 */
function keepCodesForTerms(
  codes: BuyerResearchIndustryCodesDto[] | null,
  terms: string[],
): BuyerResearchIndustryCodesDto[] | null {
  if (!codes) return null;
  const live = new Set(terms.map((t) => t.toLowerCase()));
  return codes.filter((c) => live.has(c.term.toLowerCase()));
}

/**
 * The five steps, in order. The INDEX is the step, so this array is the one
 * place their order is stated.
 *
 * <p>🔴 <b>Grouped by what a field DOES (R4).</b> `exclusions` sits with
 * `notes` on the first step rather than with the money floors, because neither
 * of them narrows the search — putting a field that only feeds ranking beside
 * the ones that reach the vendor tells a specialist they are filtering when
 * they are not.</p>
 */
const STEP_KEYS = ["looking", "spend", "where", "ask", "review"] as const;
type StepKey = (typeof STEP_KEYS)[number];

const STEP_LABEL_KEY: Record<StepKey, "stepLooking" | "stepSpend" | "stepWhere" | "stepAsk" | "stepReview"> = {
  looking: "stepLooking",
  spend: "stepSpend",
  where: "stepWhere",
  ask: "stepAsk",
  review: "stepReview",
};

/**
 * 🔴 EVERY FIELD THE RUN IS GIVEN, MAPPED TO THE REVIEW ROW THAT SHOWS IT.
 *
 * <p>Step 5 promises "everything the run will be given". A hand-written list of
 * rows cannot keep that promise: adding a `Draft` member and forgetting the row
 * leaves the promise false and every test green, which is exactly how
 * `statedPriceBand` — carried onto the brief by `fromDraft` and rendered only
 * inside the spend step — stayed off the review.</p>
 *
 * <p>`satisfies Record&lt;keyof Draft | "statedPriceBand", ...&gt;` makes the
 * omission a COMPILE ERROR rather than a test that has to think of it. A member
 * that is deliberately not shown maps to `null` and has to say so in one place,
 * so "not on the review" is always a decision somebody made.</p>
 *
 * <p>The values are `data-field` attributes on the rendered rows, which is what
 * lets a test check the rows actually reach the screen rather than checking this
 * map against itself.</p>
 */
const REVIEW_COVERAGE = {
  industries: "industries",
  industryCodes: "industryCodes",
  notes: "notes",
  exclusions: "exclusions",
  askingPriceMin: "askingPrice",
  askingPriceMax: "askingPrice",
  cashFlowMin: "cashFlowMin",
  revenueMin: "revenueMin",
  sellerFinancing: "sellerFinancing",
  willRelocate: "willRelocate",
  geographies: "geographies",
  statedPriceBand: "statedPriceBand",
  // Provenance for the fields above — rendered as a caption beside each one,
  // never a field of its own, and not something the run is given.
  scalarProv: null,
} satisfies Record<keyof Draft | "statedPriceBand", string | null>;

/** The row ids step 5 must render, derived from the map above. */
export const REVIEW_REQUIRED_FIELDS: readonly string[] = Array.from(
  new Set(Object.values(REVIEW_COVERAGE).filter((v): v is string => v !== null)),
);

interface ReviewRow {
  field: string;
  step: StepKey;
  label: string;
  value: ReactNode | null;
}

/**
 * The rail: the steps, what each currently holds, and a way back to any of
 * them.
 *
 * <p>🔴 <b>It carries VALUES, not just labels.</b> A rail of labels is a
 * progress bar — it tells a specialist where they are and nothing about what
 * they have said. The summary under each label is what makes clicking back to
 * a step worth doing, and it is what lets somebody on step 5 see that step 2
 * is empty without going there.</p>
 *
 * <p>Every step is reachable at any time. This is a brief being composed, not
 * a payment being taken: there is no state that has to be established before a
 * later one is safe to look at, and the one thing that CAN be wrong — a money
 * field that will not parse — is marked here rather than blocking the walk.</p>
 */
function StepRail({
  step,
  onSelect,
  summaries,
  errorStep,
}: {
  step: number;
  onSelect: (n: number) => void;
  summaries: readonly string[];
  errorStep: number | null;
}) {
  const copy = researchCopy.drawer;
  return (
    <nav aria-label={copy.stepsLabel} data-testid="research-brief-rail">
      <ol className="m-0 grid list-none gap-space-1 p-0">
        {STEP_KEYS.map((key, i) => {
          const isCurrent = i === step;
          const hasError = errorStep === i;
          return (
            <li key={key}>
              <button
                type="button"
                onClick={() => onSelect(i)}
                aria-current={isCurrent ? "step" : undefined}
                aria-label={copy.stepGoTo(copy[STEP_LABEL_KEY[key]])}
                data-testid="research-brief-step"
                data-step={key}
                data-current={isCurrent ? "true" : undefined}
                data-error={hasError ? "true" : undefined}
                className={cn(
                  "grid w-full gap-[2px] rounded-md border px-space-2 py-space-1 text-left",
                  isCurrent
                    ? "border-gold bg-gold/10"
                    : "border-transparent hover:border-border-light hover:bg-white-warm",
                )}
              >
                <span
                  className={cn(
                    "text-caption uppercase tracking-wide",
                    isCurrent ? "text-onyx font-semibold" : "text-gray-mid",
                  )}
                >
                  {copy[STEP_LABEL_KEY[key]]}
                </span>
                <span
                  className={cn(
                    "text-caption break-words",
                    hasError ? "text-status-error" : "text-gray-mid",
                  )}
                >
                  {hasError ? copy.stepNeedsAttention : summaries[i]}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * The search brief, editable, before a run.
 *
 * <p>Every field carries where its value came from — the buyer record, the
 * interview, the biography, the prequalification, or the model's own proposal
 * — and the moment a specialist touches one, its provenance becomes theirs.
 * The run then records the brief exactly as edited, so the run history can
 * later say which parts of a search the specialist chose and which Phoenix
 * proposed (D2).</p>
 *
 * <p>Mounted PER OPEN by the tab (unmounted on close), so the draft is seeded
 * from `initialBrief` once and never re-hydrated under a specialist's edits.</p>
 *
 * <p>A daily-limit refusal is a decision the server made about spend, not a
 * failure of the request — it renders inline with the time the next run is
 * allowed, where a toast would have vanished before the specialist read the
 * hour.</p>
 */
export function ResearchBriefForm({
  open,
  buyerLeadId,
  initialBrief,
  guards,
  resume,
  onClose,
  onStarted,
}: {
  open: boolean;
  buyerLeadId: string;
  initialBrief: BuyerResearchBriefDto;
  /**
   * The two guards that can refuse a run, as the brief endpoint read them.
   * Optional so a caller that has not been rebuilt against the wrapper still
   * compiles; the conversation replaces them with whatever it last measured.
   */
  guards?: BuyerResearchConversationGuards | null;
  /** A conversation parked on a previous visit, or null on a first one. */
  resume?: BuyerResearchConversationResume | null;
  onClose: () => void;
  onStarted: (run: BuyerResearchRunDto) => void;
}) {
  const copy = researchCopy.drawer;
  const { dateTime } = useDateFormatter();
  /**
   * 🔴 **THE RESUMED BRIEF IS ADOPTED, and the server contemplates exactly
   * that.** `BuyerResearchBriefReadDto` says its guards describe `brief` and
   * not `resume.brief` precisely because a client is free to adopt the parked
   * one — "for as long as it takes to count, which is the first thing the
   * drawer does". Picking up a conversation means picking up the query it was
   * about; opening on a freshly proposed brief under a transcript that argues
   * about a different one is the mismatch this feature exists to remove.
   */
  const [draft, setDraft] = useState<Draft>(() => toDraft(resume?.brief ?? initialBrief));
  const [newIndustry, setNewIndustry] = useState("");
  const [invalidField, setInvalidField] = useState<MoneyField | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * 🔴 **WS4's carrier, and the client is the only thing that can set it.**
   * The adaptive probe narrows a query on the way to the vendor; a specialist
   * who iterated this one against free counts must not have it narrowed again
   * underneath them. Nothing server-side can confirm a conversation happened
   * (RE2 keeps the transcript here), so this flag is the fact, and it is set
   * by the conversation reporting that a turn was taken — never by the mere
   * presence of the panel.
   */
  const [settledInConversation, setSettledInConversation] = useState(
    // 🔴 SEEDED FROM THE RESUME, and the stepped form is what makes that
    // necessary. The conversation reports this up, and it only reports when it
    // MOUNTS — which, with one step on screen at a time, is when somebody
    // opens the Ask step. A specialist resuming a query they settled last week
    // and running it without re-opening that step would otherwise be treated
    // as never having said anything: the server would probe and narrow the
    // very query they settled, which is the one thing this flag prevents.
    //
    // Fail-closed either way (an unsettled run is narrowed, not refused), so
    // the old behaviour was safe and wrong. Reading the transcript here is the
    // same evidence the conversation itself derives `settled` from, one level
    // up, so the two cannot disagree.
    () => (resume?.transcript ?? []).some((e) => e.role === "specialist"),
  );

  /**
   * 🔴 THE COUNT IS OWNED HERE, AND CALLED EXACTLY ONCE (WS3).
   *
   * It used to live inside {@link ResearchConversation} and be mirrored up
   * through an `onCountChange` callback, so this component held a COPY that was
   * only ever as fresh as the last callback. Two surfaces read the number now —
   * the conversation, which strikes it through when it goes stale, and the run
   * button, which refuses to name it — so it is held in one place and read in
   * two. Calling the hook again in a child would be a second count: two vendor
   * reads, two answers, and no way to say which one the button meant.
   */
  const counting = useResearchCount(buyerLeadId, guards ?? null);
  const { count } = counting;

  const start = useStartResearchRun(buyerLeadId);

  const [step, setStep] = useState(0);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const isLastStep = step === STEP_KEYS.length - 1;
  /**
   * Whether the step's own body already reports the count, in which case the
   * footer yields rather than saying it twice. Only the Ask step does: its
   * panel heads the transcript with the current figure, the caption and the
   * Count-again control, all of which belong together and in that context.
   */
  const bodyCarriesTheCount = STEP_KEYS[step] === "ask";

  /**
   * 🔴 The conversation's own state, owned HERE for the same reason the count
   * is: one step renders at a time, so `ResearchConversation` unmounts the
   * moment somebody steps off it. See {@link useResearchConversationState}.
   */
  const conversation = useResearchConversationState();

  /**
   * Initial focus, per step.
   *
   * <p>`Modal`'s own trap covers the whole CARD — header X, body and footer —
   * which is what keeps Back/Next/Cancel inside the Tab cycle, so it is left
   * in charge (`manageFocus` defaults true). What it cannot do is re-run
   * initial focus per step: its `initialFocusRef` effect keys on the ref's
   * IDENTITY, which does not change when a step does. Six lines here supply
   * that, and run after the trap's own effect because a parent's effects run
   * after its children's.</p>
   */
  const stepBodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const first = stepBodyRef.current?.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
  }, [open, step]);

  /**
   * 🔴 THE UNSAVED-CHANGES GUARD, RE-PROVIDED BY THIS CALLER BECAUSE NEITHER
   * PRIMITIVE HAS ONE.
   *
   * <p>`Modal` and `ResourceDrawer` both funnel all three close routes —
   * Escape, the backdrop and the header X — through a single `onClose` prop
   * and neither offers a way to refuse. That is what makes a caller-side guard
   * COMPLETE rather than a compromise: there is no fourth route that bypasses
   * it. The backdrop is additionally disabled on this dialog, so the two live
   * routes are Escape and the X.</p>
   *
   * <p>Dirty is "the fields differ from what was seeded", compared as JSON
   * against a snapshot taken once.</p>
   *
   * <p>🔴 <b>IT IS NOT ONLY `draft`, AND THE TWO EXTRAS ARE THE ONES A
   * SPECIALIST ACTUALLY LOSES.</b> Typed text becomes part of `draft` only when
   * it is COMMITTED — an industry when Add is pressed, a sentence when the turn
   * is sent. Until then it lives in its own state, so a guard over `draft`
   * alone reads "nothing to lose" while a half-typed industry or an unsent
   * question is on screen, and Escape takes it with no prompt.</p>
   *
   * <p>The conversation's TRANSCRIPT is still deliberately excluded and the
   * reason is sound — it is persisted server-side per turn and offered back on
   * the next open, so leaving does not lose it. That argument covers SENT
   * turns and stops exactly there: the composer's unsent words are the one
   * thing in the conversation that is not persisted anywhere, which is why
   * they are counted here and the transcript is not.</p>
   */
  const seededDraftJson = useRef<string | null>(null);
  if (seededDraftJson.current === null) seededDraftJson.current = JSON.stringify(draft);
  const dirty =
    JSON.stringify(draft) !== seededDraftJson.current ||
    newIndustry.trim().length > 0 ||
    conversation.instruction.trim().length > 0;

  const requestClose = () => {
    if (start.isPending) return;
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const setScalar = <K extends keyof Draft>(field: K, value: Draft[K]) =>
    setDraft((d) => ({
      ...d,
      [field]: value,
      scalarProv: { ...d.scalarProv, [field]: SPECIALIST },
    }));

  const addIndustry = () => {
    const value = newIndustry.trim();
    if (!value) return;
    setDraft((d) => ({ ...d, industries: [...d.industries, { value, prov: SPECIALIST }] }));
    setNewIndustry("");
  };

  const removeIndustry = (index: number) =>
    setDraft((d) => ({ ...d, industries: d.industries.filter((_, i) => i !== index) }));

  /**
   * Prune one code off a term. 🔴 The ENTRY survives even when its last code goes
   * (RA6): an entry with no codes is a specialist's answer, and removing it
   * entirely reads as "unasked" and is re-resolved on the next run.
   */
  const removeIndustryCode = (term: string, code: string) =>
    setDraft((d) => ({
      ...d,
      industryCodes:
        d.industryCodes?.map((entry) =>
          entry.term === term
            ? { ...entry, codes: entry.codes.filter((c) => c.code !== code) }
            : entry,
        ) ?? null,
    }));

  const setGeography = (index: number, patch: Partial<GeographyDraft>) =>
    setDraft((d) => ({
      ...d,
      geographies: d.geographies.map((g, i) =>
        i === index ? { ...g, ...patch, prov: SPECIALIST } : g,
      ),
    }));

  const addGeography = () =>
    setDraft((d) => ({
      ...d,
      geographies: [...d.geographies, { state: "", metro: "", prov: SPECIALIST }],
    }));

  const removeGeography = (index: number) =>
    setDraft((d) => ({ ...d, geographies: d.geographies.filter((_, i) => i !== index) }));

  /**
   * The working brief as the fields currently hold it, or null while a money
   * field refuses to parse. Recomputed each render rather than memoised: it is
   * a handful of trims over a small object, and a stale memo here would be a
   * count taken against a query the specialist has already moved on from.
   */
  const currentBriefResult = fromDraft(draft, initialBrief.statedPriceBand);
  const currentBrief = "brief" in currentBriefResult ? currentBriefResult.brief : null;
  const currentBriefJson = currentBrief ? JSON.stringify(currentBrief) : null;
  const countStale = counting.isStaleFor(currentBriefJson);

  const run = () => {
    setError(null);
    const result = fromDraft(draft, initialBrief.statedPriceBand);
    if ("invalid" in result) {
      setInvalidField(result.invalid);
      // Every money field is on the spend step, so a refusal here has one
      // place to send somebody. A disabled Run with a mark three steps away
      // is a dead end; the rail marks it AND this walks them to it.
      setStep(STEP_KEYS.indexOf("spend"));
      return;
    }
    setInvalidField(null);
    // 🔴 **THE ONE START-RUN CALL SITE, and the flag rides the BRIEF.** WS4
    // carries it through `BriefJson` because `StartRunAsync` only QUEUES and
    // that is the single free-form column written at queue time; a sibling flag
    // on the request would be a second place the same fact could be asserted,
    // free to disagree with this one.
    start.mutate({ ...result.brief, settledInConversation }, {
      onSuccess: (r) => {
        onStarted(r);
        onClose();
      },
      onError: (e: unknown) => {
        if (isDailyLimitRefusal(e)) {
          const next = dailyLimitNextAllowedAt(e);
          setError(copy.dailyLimit(next ? dateTime(next) : null));
        } else {
          setError(extractErrorMessage(e));
        }
      },
    });
  };

  /**
   * Next. The only step that can refuse is the money one, and it refuses for
   * the only reason a field here can be wrong: a figure that will not parse.
   * Refusing there rather than at Run is the difference between being told now
   * and being walked back three steps later.
   */
  const goNext = () => {
    if (STEP_KEYS[step] === "spend") {
      const result = fromDraft(draft, initialBrief.statedPriceBand);
      if ("invalid" in result) {
        setInvalidField(result.invalid);
        return;
      }
      setInvalidField(null);
    }
    setStep((n) => Math.min(n + 1, STEP_KEYS.length - 1));
  };

  const money = (value: string): string | null => {
    const parsed = parseMoneyInput(value);
    return parsed == null ? null : formatCurrency(parsed);
  };

  const industryList = draft.industries.map((i) => i.value).filter(Boolean);
  const geographyList = draft.geographies
    .map((g) => [g.metro.trim(), g.state.trim()].filter(Boolean).join(", "))
    .filter(Boolean);

  /**
   * What each step currently holds, for the rail. Recomputed each render: it
   * is a handful of joins over a small object, and a memo here would be one
   * more thing that can be stale about a brief somebody is editing.
   */
  const railSummaries: readonly string[] = [
    industryList.length > 0 ? industryList.join(" · ") : copy.summaryNoIndustries,
    [money(draft.askingPriceMin), money(draft.askingPriceMax), money(draft.cashFlowMin), money(draft.revenueMin)]
      .filter(Boolean).length > 0
      ? [
          money(draft.askingPriceMin) || money(draft.askingPriceMax)
            ? `${money(draft.askingPriceMin) ?? "—"} to ${money(draft.askingPriceMax) ?? "—"}`
            : null,
          money(draft.cashFlowMin) ? `${copy.cashFlowMin}: ${money(draft.cashFlowMin)}` : null,
          money(draft.revenueMin) ? `${copy.revenueMin}: ${money(draft.revenueMin)}` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : copy.summaryNoMoney,
    draft.willRelocate === true
      ? copy.summaryRelocating
      : geographyList.length > 0
        ? geographyList.join(" · ")
        : copy.summaryNoGeographies,
    conversation.entries.some((e) => e.role === "specialist")
      ? [
          copy.summaryAskTurns(conversation.entries.filter((e) => e.role === "specialist").length),
          count != null && !countStale ? copy.summaryCount(count.matchCount) : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : copy.summaryAskNotStarted,
    invalidField ? copy.summaryReviewMoney : copy.summaryReviewReady,
  ];

  /**
   * 🔴 R11(2) — EVERY field, read-only, each row clicking through to its step.
   * A review that shows a subset invites a specialist to trust that what it
   * omits is empty, which on a brief is the difference between "no floor" and
   * "a floor I forgot I set".
   */
  const reviewRows: readonly ReviewRow[] = [
    { field: "industries", step: "looking", label: copy.industries,
      value: industryList.length > 0 ? industryList.join(", ") : null },
    { field: "industryCodes", step: "looking", label: copy.industryCodes,
      value: draft.industryCodes?.length
        ? draft.industryCodes
            .map((e) => `${e.term}: ${e.codes.length > 0 ? e.codes.map((c) => c.code).join(", ") : "—"}`)
            .join(" · ")
        : null },
    // 🔴 TWO ROWS, NOT ONE. Step 1 groups these under a single label because
    // they do the same JOB there; the review's job is different — it says what
    // the run is given, and one row joined with an em-dash leaves a reader who
    // set only one of them unable to tell which.
    { field: "notes", step: "looking", label: copy.notes, value: draft.notes.trim() || null },
    { field: "exclusions", step: "looking", label: copy.exclusions,
      value: draft.exclusions.trim() || null },
    { field: "askingPrice", step: "spend", label: copy.askingPriceMin.replace(" — minimum", ""),
      value: money(draft.askingPriceMin) || money(draft.askingPriceMax)
        ? `${money(draft.askingPriceMin) ?? "—"} to ${money(draft.askingPriceMax) ?? "—"}`
        : null },
    { field: "cashFlowMin", step: "spend", label: copy.cashFlowMin, value: money(draft.cashFlowMin) },
    { field: "revenueMin", step: "spend", label: copy.revenueMin, value: money(draft.revenueMin) },
    { field: "sellerFinancing", step: "spend", label: copy.sellerFinancing,
      value: researchCopy.sellerFinancing[draft.sellerFinancing] },
    { field: "willRelocate", step: "where", label: copy.willRelocate,
      value: draft.willRelocate === true
        ? copy.willRelocateYes
        : draft.willRelocate === false
          ? copy.willRelocateNo
          : copy.willRelocateUnknown },
    { field: "geographies", step: "where", label: copy.geographies,
      // 🔴 A relocating brief sends no geography at all, so listing the rows
      // here would show a specialist places the vendor will never be asked
      // about — on the one screen whose job is to say what the run gets.
      value: draft.willRelocate === true
        ? copy.summaryRelocating
        : geographyList.length > 0
          ? geographyList.join(", ")
          : null },
    { field: "statedPriceBand", step: "spend", label: copy.reviewStatedPriceBand,
      value: initialBrief.statedPriceBand ?? null },
    { field: "ask", step: "ask", label: copy.stepAsk,
      value: conversation.entries.some((e) => e.role === "specialist")
        ? copy.summaryAskTurns(conversation.entries.filter((e) => e.role === "specialist").length)
        : null },
  ];

  const stepBody = (() => {
    switch (STEP_KEYS[step]) {
      case "looking":
        return (
          <div className="grid gap-space-4">
{/* Industries */}
      <fieldset className="m-0 border-0 p-0 grid gap-space-2">
        <legend className="text-caption uppercase tracking-wide text-gray-mid">
          {copy.industries}
        </legend>
        <ul className="m-0 p-0 list-none flex flex-wrap gap-space-2" data-testid="research-industries">
          {draft.industries.map((ind, i) => (
            <li
              key={`${i}-${ind.value}`}
              className="inline-flex flex-col gap-[2px] rounded-md border border-border-light px-space-2 py-space-1"
            >
              <span className="inline-flex items-center gap-space-1 text-small text-onyx">
                {ind.value}
                <button
                  type="button"
                  onClick={() => removeIndustry(i)}
                  aria-label={copy.industryRemove(ind.value)}
                  className="inline-flex text-gray-mid hover:text-onyx"
                >
                  <X size={12} aria-hidden />
                </button>
              </span>
              <ProvenanceCaption label={ind.prov} />
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-space-2">
          <Input
            value={newIndustry}
            onChange={(e) => setNewIndustry(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addIndustry();
              }
            }}
            placeholder={copy.industriesPlaceholder}
            aria-label={copy.industriesAdd}
            className="max-w-xs"
            data-testid="research-industry-input"
          />
          <Button size="sm" variant="secondary" onClick={addIndustry} disabled={!newIndustry.trim()}>
            {copy.industriesAddButton}
          </Button>
        </div>
      </fieldset>

{/* Resolved industry codes (D6 / RA6) */}
      {draft.industryCodes && draft.industryCodes.length > 0 && (
        <fieldset
          className="m-0 border-0 p-0 grid gap-space-2"
          data-testid="research-industry-codes"
        >
          <legend className="text-caption uppercase tracking-wide text-gray-mid">
            {copy.industryCodes}
          </legend>
          <p className="m-0 text-caption text-gray-mid">{copy.industryCodesHint}</p>
          <ul className="m-0 p-0 list-none grid gap-space-2">
            {draft.industryCodes.map((entry) => (
              <li key={entry.term} data-testid="research-industry-code-term" data-term={entry.term}>
                <span className="text-small text-onyx">{entry.term}</span>
                {entry.codes.length > 0 ? (
                  <ul className="m-0 mt-[2px] p-0 list-none flex flex-wrap gap-space-1">
                    {entry.codes.map((c) => (
                      <li
                        key={c.code}
                        className="inline-flex items-center gap-space-1 rounded-md border border-border-light px-space-2 py-[2px] text-caption text-onyx"
                        data-testid="research-industry-code"
                      >
                        <span className="tabular-nums">{c.code}</span>
                        <span className="text-gray-mid">{c.title}</span>
                        {c.matchedVia && (
                          <span className="text-gray-mid">({copy.industryCodeVia(c.matchedVia)})</span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeIndustryCode(entry.term, c.code)}
                          aria-label={copy.industryCodeRemove(c.code, entry.term)}
                          className="inline-flex text-gray-mid hover:text-onyx"
                        >
                          <X size={12} aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  // 🔴 Two different sentences for one empty list, because they
                  // are two different facts: Clef matched nothing, or a person
                  // decided none of the suggestions belonged.
                  <p
                    className="m-0 mt-[2px] text-caption text-gray-mid"
                    data-testid="research-industry-code-empty"
                  >
                    {entry.unresolved
                      ? copy.industryCodeUnresolved(entry.unresolvedReason)
                      : copy.industryCodesPruned}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </fieldset>
      )}

            {/* 🔴 R4 — the pair is LABELLED, because the one thing a reader
                could get wrong here is thinking it narrows the search. It does
                not: `exclusions` and `notes` both reach the ranking prompt as
                context, and neither reaches the vendor query. */}
            <div className="grid gap-space-2 rounded-md border border-border-light p-space-3">
              <div className="grid gap-[2px]">
                <span className="text-caption uppercase tracking-wide text-gray-mid">
                  {copy.contextPair}
                </span>
                <span className="text-caption text-gray-mid">{copy.contextPairHint}</span>
              </div>
{/* Exclusions + notes */}
      <label className="block">
        <span className="text-caption uppercase tracking-wide text-gray-mid">{copy.exclusions}</span>
        <Textarea
          rows={2}
          value={draft.exclusions}
          onChange={(e) => setScalar("exclusions", e.target.value)}
          placeholder={copy.exclusionsPlaceholder}
          aria-label={copy.exclusions}
          className="mt-space-1"
          data-testid="research-exclusions"
        />
        <span className="mt-space-1 block">
          <ProvenanceCaption label={draft.scalarProv.exclusions} />
        </span>
      </label>
      <label className="block">
        <span className="text-caption uppercase tracking-wide text-gray-mid">{copy.notes}</span>
        <Textarea
          rows={3}
          value={draft.notes}
          onChange={(e) => setScalar("notes", e.target.value)}
          placeholder={copy.notesPlaceholder}
          aria-label={copy.notes}
          className="mt-space-1"
          data-testid="research-notes"
        />
        <span className="mt-space-1 block">
          <ProvenanceCaption label={draft.scalarProv.notes} />
        </span>
      </label>
            </div>
          </div>
        );

      case "spend":
        return (
          <div className="grid gap-space-4">
{/* Money */}
      <div className="grid gap-space-3 sm:grid-cols-2">
        {MONEY_FIELDS.map((f) => (
          <label key={f} className="block">
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              {copy[f]}
            </span>
            {/* text + decimal, never type="number": a number input reports ""
                for both "cleared" and "malformed", which defeats telling an
                unset floor from a mistyped one. */}
            <Input
              type="text"
              inputMode="decimal"
              value={draft[f]}
              onChange={(e) => {
                setInvalidField((cur) => (cur === f ? null : cur));
                setScalar(f, e.target.value);
              }}
              placeholder={copy.moneyPlaceholder}
              invalid={invalidField === f}
              aria-label={copy[f]}
              className="mt-space-1"
              data-testid={`research-${f}`}
            />
            <span className="mt-space-1 flex flex-wrap items-center justify-between gap-space-2">
              <ProvenanceCaption label={draft.scalarProv[f]} />
              {invalidField === f ? (
                <span className="text-caption text-status-error">{copy.invalidMoney}</span>
              ) : (
                parseMoneyInput(draft[f]) != null && (
                  <span className="text-caption text-gray-mid tabular-nums">
                    {formatCurrency(parseMoneyInput(draft[f]) ?? 0)}
                  </span>
                )
              )}
            </span>
          </label>
        ))}
      </div>

{/* Seller financing */}
      <label className="block">
        <span className="text-caption uppercase tracking-wide text-gray-mid">
          {copy.sellerFinancing}
        </span>
        <select
          value={draft.sellerFinancing}
          onChange={(e) => setScalar("sellerFinancing", e.target.value as BuyerResearchSellerFinancing)}
          aria-label={copy.sellerFinancing}
          className="mt-space-1 block w-full max-w-xs h-10 px-space-3 font-body text-small text-onyx bg-white-pure border border-border-light rounded-md focus:outline-none focus:border-gold focus:shadow-goldRing"
          data-testid="research-seller-financing"
        >
          {SELLER_FINANCING_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {researchCopy.sellerFinancing[o]}
            </option>
          ))}
        </select>
        <span className="mt-space-1 block">
          <ProvenanceCaption label={draft.scalarProv.sellerFinancing} />
        </span>
      </label>

            {initialBrief.statedPriceBand && (
              <p className="m-0 text-caption text-gray-mid" data-testid="research-stated-price-band">
                {researchCopy.tab.statedPriceBandPrefix} {initialBrief.statedPriceBand} ·{" "}
                {researchCopy.tab.statedPriceBandNote}
              </p>
            )}
          </div>
        );

      case "where":
        return (
          <div className="grid gap-space-4">
{/* Will relocate */}
      <fieldset className="m-0 border-0 p-0 grid gap-space-1">
        <legend className="text-caption uppercase tracking-wide text-gray-mid">
          {copy.willRelocate}
        </legend>
        <div className="flex flex-wrap gap-space-3 text-small" role="radiogroup" aria-label={copy.willRelocate}>
          {(
            [
              [true, copy.willRelocateYes],
              [false, copy.willRelocateNo],
              [null, copy.willRelocateUnknown],
            ] as const
          ).map(([value, label]) => (
            <label key={String(value)} className="inline-flex items-center gap-space-1">
              <input
                type="radio"
                name="research-will-relocate"
                checked={draft.willRelocate === value}
                onChange={() => setScalar("willRelocate", value)}
              />
              {label}
            </label>
          ))}
        </div>
        <ProvenanceCaption label={draft.scalarProv.willRelocate} />
      </fieldset>

            {/* 🔴 RA2 — a relocating brief sends NO states and no cities. The
                rows below stay editable and stop reaching the vendor, which is
                invisible unless something says so. */}
            {draft.willRelocate === true && (
              <p
                className="m-0 rounded-md border border-status-warning bg-status-warning/12 p-space-3 text-small text-onyx"
                data-testid="research-relocate-warning"
              >
                {copy.relocateIgnoresGeography}
              </p>
            )}

{/* Geographies */}
      <fieldset className="m-0 border-0 p-0 grid gap-space-2">
        <legend className="text-caption uppercase tracking-wide text-gray-mid">
          {copy.geographies}
        </legend>
        <ul className="m-0 p-0 list-none grid gap-space-2" data-testid="research-geographies">
          {draft.geographies.map((g, i) => (
            <li key={i} className="grid gap-space-1">
              <div className="flex flex-wrap items-end gap-space-2">
                <label className="block">
                  <span className="text-caption text-gray-mid">{copy.geographyState}</span>
                  <Input
                    value={g.state}
                    onChange={(e) => setGeography(i, { state: e.target.value })}
                    aria-label={`${copy.geographyState} ${i + 1}`}
                    className="w-24 mt-space-1"
                    maxLength={2}
                  />
                </label>
                <label className="block flex-1 min-w-0">
                  <span className="text-caption text-gray-mid">{copy.geographyMetro}</span>
                  <Input
                    value={g.metro}
                    onChange={(e) => setGeography(i, { metro: e.target.value })}
                    aria-label={`${copy.geographyMetro} ${i + 1}`}
                    className="mt-space-1"
                  />
                </label>
                <Button
                  size="sm"
                  variant="text"
                  onClick={() => removeGeography(i)}
                  aria-label={copy.geographyRemove(i)}
                >
                  <X size={14} aria-hidden />
                </Button>
              </div>
              <ProvenanceCaption label={g.prov} />
            </li>
          ))}
        </ul>
        <div>
          <Button size="sm" variant="secondary" onClick={addGeography}>
            {copy.geographyAdd}
          </Button>
        </div>
      </fieldset>
          </div>
        );

      case "ask":
        return (
          <div className="grid gap-space-4">
            <ResearchConversation
              buyerLeadId={buyerLeadId}
              brief={currentBrief}
              counting={counting}
              state={conversation}
              initialResume={resume ?? null}
              onApplyBrief={(b) => setDraft(toDraft(b))}
              onSettledChange={setSettledInConversation}
              canEdit
            />
            {/* 🔴 R8 — the offer sentence is the SERVER's, approved verbatim
                and pinned against the capability catalogue, so it is not
                rewritten. This is the caveat beside it, and it says what
                accepting actually does. */}
            <p className="m-0 text-caption text-gray-mid" data-testid="research-route-caveat">
              {researchCopy.conversation.routeCaveat}
            </p>
          </div>
        );

      case "review":
        return (
          <div className="grid gap-space-3">
            <p className="m-0 text-caption text-gray-mid">{copy.reviewHint}</p>
            {/* 🔴 A LIST OF BUTTONS, NOT A DESCRIPTION LIST. Every row here is a
                CONTROL — it navigates to the step that owns the field — and a
                `<button>` is not a permitted child of `<dl>`, so the obvious
                markup is invalid and hands assistive technology a description
                list whose pairs are not pairs.

                The pairing reads fine without `<dl>`: each button's accessible
                name is "Change {label}", so a screen reader announces the field
                and the action together, and the value follows in the same
                control. Wrapping the whole row in the button (rather than the
                value alone) is what makes the label clickable, which is what
                the ruling asked for. */}
            <ul className="m-0 grid list-none gap-[2px] p-0" data-testid="research-review">
              {reviewRows.map((row) => (
                <li key={row.field} className="m-0">
                  <button
                    type="button"
                    onClick={() => setStep(STEP_KEYS.indexOf(row.step))}
                    aria-label={copy.reviewEdit(row.label)}
                    className="grid w-full grid-cols-[minmax(0,14rem)_1fr] items-baseline gap-space-3 rounded-md border border-transparent px-space-2 py-space-1 text-left hover:border-border-light hover:bg-white-warm"
                    data-testid="research-review-row"
                    data-field={row.field}
                  >
                    <span className="text-caption uppercase tracking-wide text-gray-mid">
                      {row.label}
                    </span>
                    <span className="text-small text-onyx break-words">
                      {row.value ?? <span className="text-gray-mid">{copy.reviewEmpty}</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {/* 🔴 R6 — the re-run guard is a SOURCE SKIP at execution time, not
                a refusal. Saying "we would refuse" reads as "the button will
                not work", and then the run starts, Clef is skipped, and an
                empty list comes back with no account of why. */}
            {counting.guards?.reRunGuardWouldRefuse && (
              <p
                className="m-0 rounded-md border border-status-warning bg-status-warning/12 p-space-3 text-small text-onyx"
                role="status"
                data-testid="research-review-rerun-guard"
              >
                {researchCopy.conversation.reRunWouldSkipClef(
                  counting.guards.identicalRunAtUtc
                    ? dateTime(counting.guards.identicalRunAtUtc)
                    : "",
                  counting.guards.reRunWindowHours,
                )}
              </p>
            )}

            {/* The caption used to render HERE, because step 5 was the only
                place the figure appeared beside the button. It now rides the
                footer on every step, so a copy here would state the same
                sentence twice on one screen. The gate on the figure is
                unchanged and is NOT re-opened: the number is named only where
                Phoenix can keep it. */}
          </div>
        );
    }
  })();

  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        // A five-step form is a task somebody completes, and a centred dialog
        // has a far larger backdrop target than a right-anchored drawer did.
        // Losing the whole brief to a stray click outside it is not a trade
        // worth making; Escape and the X still close, both through the dirty
        // guard below.
        dismissOnBackdropClick={false}
        size="lg"
        title={copy.title}
        description={copy.subtitle}
        footer={
          <div className="flex flex-1 flex-wrap items-center justify-between gap-space-3">
            {/* 🔴 THE PAIR SITS HERE SO THE FIGURE IS BESIDE THE BUTTON, which
                is the ruling, and so it is REPORTED on every step that has one
                rather than only on the step that makes the promise.

                The BUTTON names the figure only where Phoenix can keep it — the
                two conditions in its own block below. That gate is about
                stating a number as THE FIGURE THE BUY WILL HONOUR. REPORTING
                the count is a different act: withholding it until the last step
                would leave a specialist who has stepped BACK to correct a field
                with no idea what it now matches, which is the thing a live
                count exists for.

                🔴 "Every step that has one" is narrower than "every step", and
                the difference is deliberate rather than an oversight: the count
                is FETCHED by `ResearchConversation`'s seeded effect, which runs
                when the Ask step first mounts, so a specialist who has not been
                to Ask has no count anywhere — not here, not on the button, and
                not in the panel. That is the stepped form's own doing (the
                single-page drawer mounted the conversation on open, so a count
                landed immediately) and moving the fetch up to the form's open is
                a decision about whether opening a brief should spend a vendor
                read before anybody has said what they are looking for. Reported,
                not taken here.

                Stale renders STRUCK THROUGH rather than hidden, exactly as the
                conversation panel does it. A specialist who has just edited a
                field needs to see that the number no longer describes what is
                in front of them; an absence says nothing at all.

                🔴 IT YIELDS ON THE ASK STEP, and only there. That step's own
                body renders the same figure IN CONTEXT — at the head of the
                transcript, whose turns each carry their own counts, beside the
                stale notice and the Count-again button that belong with it.
                Rendering the footer pair as well would state one sentence twice
                on one screen, a few inches apart. So the rule is: the footer
                carries the pair wherever the step's body does not, which is
                every step but Ask — and on the LAST step, which is the one the
                ruling names, it sits beside the Run button. */}
            <div className="grid min-w-0 gap-[2px]" data-testid="research-brief-footer-count">
              {count != null && !bodyCarriesTheCount && (
                <p
                  className="m-0 text-small text-onyx break-words"
                  data-testid="research-brief-footer-matches"
                  data-stale={countStale || undefined}
                >
                  {count.matchCount === 0 ? (
                    <span className="text-gray-mid">{researchCopy.conversation.noMatches}</span>
                  ) : (
                    <>
                      <span className={countStale ? "text-gray-mid line-through" : "tabular-nums"}>
                        {researchCopy.conversation.matches(count.matchCount)}
                      </span>
                      {count.approximate && (
                        <span className="text-caption text-gray-mid">
                          {" — "}
                          {researchCopy.conversation.approximate}
                        </span>
                      )}
                    </>
                  )}
                </p>
              )}

              {/* The same four conditions the conversation panel applies: there
                  is a count, it found something (nothing narrows a zero), the
                  query is not settled, and the figure is not already struck
                  through — stacking the stale notice and this one reads as two
                  problems when there is one. */}
              {count != null &&
                !bodyCarriesTheCount &&
                count.matchCount > 0 &&
                !settledInConversation &&
                !countStale && (
                  <p
                    className="m-0 text-caption text-gray-mid break-words"
                    data-testid="research-brief-footer-may-narrow"
                  >
                    {researchCopy.conversation.mayNarrowBeforeBuying}
                  </p>
                )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-space-2">
              <Button variant="text" onClick={requestClose} disabled={start.isPending}>
                {copy.cancel}
              </Button>
              {step > 0 && (
                <Button
                  variant="secondary"
                  onClick={() => setStep((n) => n - 1)}
                  disabled={start.isPending}
                  data-testid="research-brief-back"
                >
                  {copy.back}
                </Button>
              )}
              {isLastStep ? (
                <Button
                  onClick={run}
                  loading={start.isPending}
                  disabled={start.isPending}
                  data-testid="research-brief-run"
                >
                  {/* 🔴 THE NUMBER ON THIS BUTTON IS A PROMISE, SO IT IS NAMED
                      ONLY WHERE PHOENIX CAN KEEP IT — which takes BOTH conditions
                      below, and the second is the one a careless edit drops.

                      (i) The count must still describe the brief in the fields. A
                      struck-through figure beside a live button is a promise about
                      a query the specialist has already moved on from.

                      (ii) The specialist must have SETTLED the query in
                      conversation. `settledInConversation` is the same flag the
                      server reads to suppress the probe — `ClefListingSource`
                      derives `mayProbe && budget.QuerySettledInConversation` — so
                      where it is false the probe CONVERGES: `BuildFilter`'s output
                      is narrowed, the count is re-taken against the narrowed
                      query, and THAT narrowed count is what the buy honours. The
                      pre-click number is then the population BEFORE narrowing —
                      accurate about the brief, and not about the purchase.

                      Naming it there is the exact symptom this feature exists to
                      dissolve, displaced from Pull-more onto the zero-turn Run: the
                      specialist reads "Run it — 5,161 matches", clicks, and ~100
                      records are bought from a different population. The count is
                      still REPORTED — in the footer beside this button, on every
                      other step that has one, and in the conversation panel — under
                      the caption saying a broad query may narrow; what stops here is
                      stating it as the figure the buy will honour. */}
                  {start.isPending
                    ? copy.running
                    : count != null && !countStale && settledInConversation
                      ? researchCopy.conversation.runWithCount(count.matchCount)
                      : copy.run}
                </Button>
              ) : (
                <Button
                  onClick={goNext}
                  disabled={start.isPending}
                  data-testid="research-brief-next"
                >
                  {copy.next}
                </Button>
              )}
            </div>
          </div>
        }
      >
        <div className="grid gap-space-4" data-testid="research-brief-form">
          {error && (
            <p
              className="m-0 rounded-md border border-status-error bg-status-error/12 p-space-3 text-small text-status-error"
              role="alert"
              data-testid="research-brief-error"
            >
              {error}
            </p>
          )}

          <div className="grid gap-space-4 sm:grid-cols-[minmax(0,13rem)_1fr]">
            <StepRail
              step={step}
              onSelect={setStep}
              summaries={railSummaries}
              errorStep={invalidField ? STEP_KEYS.indexOf("spend") : null}
            />
            <div ref={stepBodyRef} className="min-w-0">
              {stepBody}
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={confirmDiscard}
        title={copy.discardTitle}
        message={copy.discardMessage}
        confirmLabel={copy.discardConfirm}
        cancelLabel={copy.discardCancel}
        destructive
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>
  );
}

/**
 * The brief a finished run was searched with, READ-ONLY.
 *
 * <p>🔴 <b>FOLDED BY DEFAULT UNDER `SourceLog`'S RULE, ADOPTED BY NAME: FOLD
 * THE UNIFORM, EXPOSE THE DIVERGENT.</b> That component states it as "Folded
 * into the summary only when every skipped source gave the SAME reason. One of
 * several reasons cannot speak for the others, so a mixed set leaves the count
 * standing alone and sends the reader to the rows." The same sentence governs
 * here: a one-line summary may stand in for these rows only while the rows
 * agree with each other, and when one of them does not, the summary cannot
 * speak for it and the panel stands open.</p>
 *
 * <p>🔴 <b>NOT keyed on the run having settled.</b> `hasSettledResults` is true
 * for `Failed` and `CompletedPartial` too, so folding on it would fold the
 * brief on exactly the runs that went wrong — which is when somebody is
 * reading it to find out why. Settling is not agreement.</p>
 *
 * <p><b>Two things count as divergent, and they are two readings of one rule
 * rather than a second rule.</b> (1) A SPECIALIST CORRECTED SOMETHING: every
 * other provenance kind is Phoenix's own derivation from the record, so a
 * `specialist` label is the one row that disagrees with the rest about where
 * the brief came from — and it is the row a reader is checking for. (2) A TERM
 * DID NOT REACH THE VENDOR AS CODES: the words are what a specialist typed and
 * the codes are what Clef filtered on, so a term resolving to none means the
 * search was wider than the panel's own words claim. The comment on those rows
 * already says a surprising run "is explicable only if the codes it used are on
 * the same screen as the terms that produced them" — folding them by default is
 * the SOURCES mistake, one panel over.</p>
 *
 * <p>⚠️ <b>"Uniform" is the ROUTINE case, not a same-value test, and reading it
 * on the wrong axis inverts the rule.</b> A brief a specialist rewrote end to
 * end is uniformly `specialist` and stands OPEN — it is the most hand-made
 * brief there is. `SourceLog` has the same shape at its own outer layer: what
 * folds there is the SKIP, which happens on every run, and what never folds is
 * the failure. Fold what always happens; expose the departure from it.</p>
 *
 * <p>Presentation only: local `useState`, no preference write, no request. The
 * server holds no opinion about whether this panel is open.</p>
 */
export function ResearchBriefReadOnly({
  brief,
  writtenAtUtc,
}: {
  brief: BuyerResearchBriefDto;
  /**
   * When the brief was persisted — the owning run's `queuedAt`, because
   * `StartRunAsync` writes `BriefJson` once and never again.
   *
   * <p>Optional, and absent renders the dateless hedge rather than nothing: the
   * timestamp lives on the run and not on the brief, so a caller holding only a
   * brief can still say that the panel is a snapshot.</p>
   */
  writtenAtUtc?: string | null;
}) {
  const copy = researchCopy.drawer;
  const df = useDateFormatter();
  const p = brief.provenance ?? {};

  /** One rendered field. `value: null` is a blank, not an empty string. */
  const fieldRows: { label: string; value: string | null; prov: string | undefined }[] = [];

  brief.industries.forEach((ind, i) => {
    fieldRows.push({
      label: i === 0 ? copy.industries : "",
      value: ind,
      prov: p[`industries[${i}]`],
    });
  });
  brief.geographies.forEach((g, i) => {
    const value = [g.metro, g.state].filter(Boolean).join(", ");
    fieldRows.push({
      label: i === 0 ? copy.geographies : "",
      value,
      prov: p[`geographies[${i}]`],
    });
  });
  fieldRows.push({
    label: copy.willRelocate,
    // Left as a tri-state rather than folded into the §7 blank below: the DTO
    // models `boolean | null` deliberately and "Not known" is an answer the
    // brief records, not a field nobody filled in.
    value:
      brief.willRelocate === true
        ? copy.willRelocateYes
        : brief.willRelocate === false
          ? copy.willRelocateNo
          : copy.willRelocateUnknown,
    prov: p.willRelocate,
  });
  for (const f of MONEY_FIELDS) {
    const v = brief[f];
    // §7 — a bound nobody set is a plain missing value, and there is nothing to
    // do about it on a finished run's brief, so it renders as the em dash with
    // no door. "Not set" is the "never 'None'" family the section names; it
    // stays on the drawer's INPUT, where it prompts rather than reports.
    fieldRows.push({ label: copy[f], value: v == null ? null : formatCurrency(v), prov: p[f] });
  }
  fieldRows.push({
    label: copy.sellerFinancing,
    value: researchCopy.sellerFinancing[brief.sellerFinancing] ?? brief.sellerFinancing,
    prov: p.sellerFinancing,
  });
  if (brief.exclusions) {
    fieldRows.push({ label: copy.exclusions, value: brief.exclusions, prov: p.exclusions });
  }
  if (brief.notes) fieldRows.push({ label: copy.notes, value: brief.notes, prov: p.notes });

  // What the run ACTUALLY filtered on. The words are what a specialist typed;
  // the codes are what reached the vendor, and the two can differ by every
  // pruning decision — so a past run that surprised somebody is explicable only
  // if the codes it used are on the same screen as the terms that produced them.
  //
  // Held apart from `fieldRows` because these are DERIVED from the terms above
  // rather than fields of the brief: they carry no provenance, and counting
  // them as unattributed would report a brief as less sourced than it is by one
  // row per industry term.
  const codeRows = (brief.industryCodes ?? []).map((entry, i) => ({
    label: i === 0 ? copy.industryCodes : "",
    prov: undefined,
    value: `${entry.term}: ${
      entry.codes.length > 0
        ? entry.codes.map((c) => `${c.code} ${c.title}`).join(" · ")
        : entry.unresolved
          ? copy.industryCodeUnresolved(entry.unresolvedReason)
          : copy.industryCodesPruned
    }`,
  }));

  // ONE note for the whole brief, in place of a caption under every row.
  // Summarised over the FIELD rows alone, which is the set the note sits under
  // and therefore the only set a reader can check it against.
  const provenance = summarizeProvenance(fieldRows.map((r) => r.prov));
  const sourceNames = provenance.kinds.map((k) => copy.provenance.kinds[k] ?? k);
  const provenanceNote = copy.provenance.summary(sourceNames, provenance.unattributed);

  const divergent =
    provenance.kinds.includes("specialist") ||
    (brief.industryCodes ?? []).some((e) => e.codes.length === 0);

  // The override is stored WITH the default it was made against, so a default
  // that moves discards it. Without that, a reader who folded a uniform brief
  // and then selected a divergent run would meet the divergent one folded —
  // this component is reused across runs rather than remounted, so `useState`'s
  // initial value is read once and never again. A pure derivation rather than a
  // render-phase reset: there is no state to put back.
  const [override, setOverride] = useState<{ forDefault: boolean; value: boolean } | null>(null);
  const open = override !== null && override.forDefault === divergent ? override.value : divergent;

  const locations = brief.geographies
    .map((g) => [g.metro, g.state].filter(Boolean).join(" "))
    .filter((s) => s.length > 0);

  return (
    <div className="grid gap-space-2" data-testid="research-brief-readonly" data-open={open}>
      <div className="flex flex-wrap items-center gap-space-2">
        <span className="text-caption text-gray-mid break-words" data-testid="research-brief-summary">
          {copy.briefSummary(brief.industries, locations)}
        </span>
        <Button
          type="button"
          variant="text"
          size="sm"
          aria-expanded={open}
          onClick={() => setOverride({ forDefault: divergent, value: !open })}
          data-testid="research-brief-toggle"
        >
          {open ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
          {open ? copy.briefHide : copy.briefShow}
        </Button>
      </div>

      {open && (
        <>
          {brief.statedPriceBand && (
            <p className="m-0 text-caption text-gray-mid">
              {researchCopy.tab.statedPriceBandPrefix} {brief.statedPriceBand} ·{" "}
              {researchCopy.tab.statedPriceBandNote}
            </p>
          )}
          {/* NO separating rules between the groups, and none introduced by the
              fold — a rule on some-but-not-all of a surface's divisions reads as
              a grouping signal that corresponds to no grouping, and a rule on
              every one of them would divide a definition list the label column
              already divides. §17.2's heading ladder carries the structure; the
              panel's own division is the section heading above it, in
              `ResearchTab`. */}
          <dl className="m-0 grid gap-space-1 text-small">
            {[...fieldRows, ...codeRows].map((r, i) => (
              <div key={`${r.label}-${i}`} className="grid gap-[2px] sm:grid-cols-[10rem_1fr]">
                {/* §17.4's minor label — `tracking-label` (0.14em), not
                    `tracking-wide` (0.04em). Uppercase type at this size needs
                    the wider tracking to stay legible, and §17.4 names the
                    narrow one as the drift. */}
                <dt className="font-display text-caption uppercase tracking-label text-gray-mid">
                  {r.label}
                </dt>
                <dd className="m-0 grid gap-[2px]">
                  {r.value === null ? (
                    <MissingValue />
                  ) : (
                    /* 🔴 whitespace-pre-line, because two of these rows carry prose
                       rather than a composed label. `notes` and `exclusions` are
                       written by the brief model, and ResearchConversation's
                       route-accept appends its clause after a BLANK LINE — which
                       HTML collapses, so the clause ran on from the end of the
                       sentence above it and read as a typo in something Phoenix
                       wrote. Every other row is a single line whose spaces are
                       single, so the class is a no-op for them. */
                    <span className="text-onyx break-words whitespace-pre-line">{r.value}</span>
                  )}
                  <ProvenanceCaption label={r.prov} />
                </dd>
              </div>
            ))}
          </dl>

          {/* The aggregate note and its hedge, once for the whole brief. The
              paragraph is unconditional because the hedge is: a brief whose
              every field is sourced renders no summary sentence and still needs
              to say that the panel is a snapshot. */}
          <p
            className="m-0 text-caption text-gray-mid break-words"
            data-testid="research-brief-provenance-note"
          >
            {provenanceNote ? `${provenanceNote} ` : ""}
            {writtenAtUtc ? copy.briefWrittenAt(df.dateTime(writtenAtUtc)) : copy.briefWrittenAtUnknown}
          </p>
        </>
      )}
    </div>
  );
}
